import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { selectInboundRoutes, selectTomorrowAllRoutes, selectTomorrowInboundRoutes, selectTomorrowOutboundRoutes, selectHistoryInboundRoutes, selectHistoryRoutes, selectHistoryOutboundRoutes, selectOutboundRoutes, selectAllRoutes, selectAllActiveVehicles, selectAllActiveDrivers, transRoutesSlice } from "./../../store";
import RoutesSection from "./RoutesSection";
import PersonnelSection from "./PersonnelSection";
import { AuthService, CustomerService, TransRoutesService, DriverService, EventsService, DailyRoutesTemplateService, ReportService, SignatureRequestService, EmployeeService, trackActionLog } from "../../services";
import { buildExternalDriverNameByIdMap, formatDriverDropdownLabel, sortDriversByLastName } from "../../shared/utils/driverDropdownOptions";
import { PERSONAL_ROUTE_STATUS, CUSTOMER_TYPE_TEXT, PERSONAL_ROUTE_STATUS_TEXT, PICKUP_STATUS, PICKUP_STATUS_TEXT, REPORT_TYPE, isCustomerDischarged, downloadXlsxFromMatrix, hasAddressSlot, stabilizeRouteCustomerAddressList } from "../../shared";
import { downloadDepartureBoardExcel } from "../../shared/utils/departureBoardExcel";
import { runRouteChecks, joinRouteNames, formatRouteAddressList, getCustomerTypeLabel } from "./routeChecks";
import moment from 'moment';
import DatePicker from "react-datepicker";
import { CalendarWeek, ClockHistory, Copy, Download, Plus, Clock, Filter, CalendarCheck, Check, PencilSquare } from "react-bootstrap-icons";
import { Breadcrumb, Tabs, Tab, Dropdown, Spinner, Modal, Button, ProgressBar } from "react-bootstrap";
import RouteCustomerTable from "./RouteCustomerTable";

const ROUTE_CLEAR_INBOUND_CONFIRM =
  "Are you sure you want to disable all inbound routes for this date?";
const ROUTE_CLEAR_OUTBOUND_CONFIRM =
  "Are you sure you want to disable all outbound routes for this date?";

const formatRouteCustomerDisplayName = (customer) => {
  const name = `${customer?.name || ''}`.trim();
  const nameCn = `${customer?.name_cn || ''}`.trim();
  if (!name) return '';
  return nameCn ? `${name} (${nameCn})` : name;
};

/**
 * After Import from date / Import From template: overlay latest customer Name,
 * Profile Photo, Note for Driver, Status (pickup_status), and Customer Type.
 * Address is left as on the source route; mismatches are suggested after import.
 */
const applyLatestCustomerInfoToRouteCustomer = (routeCustomer, customerById) => {
  const customer = customerById?.get(String(routeCustomer?.customer_id));
  if (!customer) return routeCustomer;

  const noteForDriver = customer.notes_for_driver || customer.special_needs || '';
  const displayName = formatRouteCustomerDisplayName(customer);

  return Object.assign({}, routeCustomer, {
    customer_name: displayName || routeCustomer.customer_name,
    customer_avatar: customer.avatar ?? routeCustomer.customer_avatar,
    customer_type: customer.type ?? routeCustomer.customer_type,
    customer_pickup_status: customer.pickup_status ?? routeCustomer.customer_pickup_status,
    customer_note: noteForDriver,
    customer_special_needs: noteForDriver,
    customer_phone: customer.phone || customer.mobile_phone || customer.home_phone || routeCustomer.customer_phone,
  });
};

/** Stable menu type for Dropdown.Menu — do not define inline in RoutesDashboard or the menu remounts every render and closes the dropdown. */
const RoutesScheduleViewDateMenu = React.forwardRef(function RoutesScheduleViewDateMenu(
  { dateSelected, onSelectViewDate, style, className, 'aria-labelledby': labeledBy },
  ref
) {
  return (
    <div
      ref={ref}
      style={style}
      className={className}
      aria-labelledby={labeledBy}
    >
      <div className="app-main-content-fields-section margin-sm dropdown-container">
        <div className="me-4">
          <div className="field-label">Select Date to View Report</div>
          <DatePicker
            selected={dateSelected}
            onChange={(v) => {
              if (v) onSelectViewDate(v);
            }}
            shouldCloseOnSelect
          />
        </div>
      </div>
    </div>
  );
});

const RoutesDashboard = () => {
  const getErrorMessageFromBlob = async (blobData, fallback = 'Failed to export route report.') => {
    try {
      if (!blobData) return fallback;
      const text = await blobData.text();
      if (!text) return fallback;
      const parsed = JSON.parse(text);
      return parsed?.message || fallback;
    } catch (_err) {
      return fallback;
    }
  };
  const getZipFilenameFromHeaders = (headers, fallback = `route_report_${Date.now()}.zip`) => {
    const rawHeader = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
    const match = rawHeader.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch (_err) {
        return match[1];
      }
    }
    return fallback;
  };
  const HIDDEN_CUSTOMER_TYPE_FILTER_VALUES = ['transferred', 'deceased', 'discharged'];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchAllRoutes, createRoute, fetchAllHistoryRoutes, fetchAllTomorrowRoutes, updateRoute }  = transRoutesSlice.actions;
  const inboundRoutes = useSelector(selectInboundRoutes);
  const outboundRoutes = useSelector(selectOutboundRoutes);
  const tmrInboundRoutes = useSelector(selectTomorrowInboundRoutes);
  const tmrOutboundRoutes = useSelector(selectTomorrowOutboundRoutes);
  const historyInboundRoutes = useSelector(selectHistoryInboundRoutes);
  const historyOutboundRoutes = useSelector(selectHistoryOutboundRoutes);
  const allRoutes = useSelector(selectAllRoutes);
  const allHistoryRoutes = useSelector(selectHistoryRoutes);
  const allTomorrowRoutes = useSelector(selectTomorrowAllRoutes);
  const drivers = useSelector(selectAllActiveDrivers);
  const vehicles = useSelector(selectAllActiveVehicles);
  const routesOverviewLoading = useSelector((s) => s?.transRoutes?.loading);
  const [showSyncCustomersLoading, setShowSyncCustomersLoading] = useState(false);
  const [keyword, setKeyword] = useState('');

  const [directorSignature, setDirectorSignature] = useState(undefined);
  const [selectedFile, setSelectedFile] = useState();
  const [selectedDriver, setSelectedDriver] = useState(undefined);
  const [signatureRouteTypeFilter, setSignatureRouteTypeFilter] = useState('');
  const [signatureHasSignatureFilter, setSignatureHasSignatureFilter] = useState('');
  const [driverList, setDriverList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const dischargedCustomerIds = useMemo(() => {
    const s = new Set();
    (customers || []).forEach((c) => {
      const id = c?.id != null ? String(c.id) : '';
      if (id && isCustomerDischarged(c)) s.add(id);
    });
    return s;
  }, [customers]);
  const [dateSelected, setDateSelected] = useState(new Date());
  const [routesForSignature, setRoutesForSignature] = useState(allRoutes);
  const [routesSignatureLoading, setRoutesSignatureLoading] = useState(false);
  /** HR / portal users assigned as driver use external_user_id; not in Redux drivers list. */
  const [externalDriverNameById, setExternalDriverNameById] = useState({});
  const [currentTab, setCurrentTab] = useState('allRoutesOverview');
  const [sorting, setSorting] = useState({key: '', order: ''});
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [routesForShowing, setRoutesForShowing] = useState(allRoutes);
  const [routesInboundForShowing, setRoutesInboundForShowing] = useState(inboundRoutes);
  const [routesOutboundForShowing, setRoutesOutboundForShowing] = useState(outboundRoutes);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [successMessage, setSuccessMessage] = useState(undefined);
  const [showCopyTodayLoading, setShowCopyTodayLoading] = useState(false);
  const [showCopyDateLoading, setShowCopyDateLoading] = useState(false);
  const [showCopyDateTargetLoading, setShowCopyDateTargetLoading] = useState(false);
  const [copyDisabled, setCopyDisabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRouteType, setDeleteRouteType] = useState('');
  const [targetedDateSelected, setTargetedDateSelected] = useState(undefined);
  const [originDateSelected, setOriginDateSelected] = useState(undefined);
  const [showOriginDateDropdown, setShowOriginDateDropdown] = useState(false);
  const [showTargetDateDropdown, setShowTargetDateDropdown] = useState(false);
  const [searchParams] = useSearchParams();
  const scheduleEditParam = searchParams.get('scheduleEdit');
  const isScheduleEditMode =
    scheduleEditParam === '1' || `${scheduleEditParam || ''}`.toLowerCase() === 'true';

  /** Use navigate + live window.location so query updates always apply (setSearchParams alone was unreliable here). */
  const setScheduleEditInUrl = (enabled) => {
    const p = new URLSearchParams(window.location.search);
    if (enabled) p.set('scheduleEdit', '1');
    else p.delete('scheduleEdit');
    const search = p.toString();
    navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, { replace: true });
  };

  const [showFilterReportDropdown, setShowFilterReportDropdown]  = useState(false);

  const [statusFilter, setStatusFilter] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [customerTableId, setCustomerTableId] = useState('');
  const [routeTypeFilter, setRouteTypeFilter] = useState('');
  
  // Save template modal state
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Daily route templates (import-from-template menu, save-as-template modal)
  const [templates, setTemplates] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [routesOverviewProgress, setRoutesOverviewProgress] = useState(0);
  const [showImportDateDropdown, setShowImportDateDropdown] = useState(false);
  const [showImportTemplateDropdown, setShowImportTemplateDropdown] = useState(false);
  const [importSourceDate, setImportSourceDate] = useState(undefined);
  const [importTemplateId, setImportTemplateId] = useState('');
  const [isScheduleImporting, setIsScheduleImporting] = useState(false);
  const [scheduleImportProgress, setScheduleImportProgress] = useState(0);
  const [scheduleImportLabel, setScheduleImportLabel] = useState('');
  const [isExportingRouteReport, setIsExportingRouteReport] = useState(false);
  const [exportRouteReportProgress, setExportRouteReportProgress] = useState(0);
  const [showCheckRoutesModal, setShowCheckRoutesModal] = useState(false);
  const [checkRoutesResult, setCheckRoutesResult] = useState({ inbound: [], outbound: [], attendance: [], addressMismatch: [], customerTypeMismatch: [], customerSpecialNeedsMismatch: [], customerNoteMismatch: [], dischargedOnRoute: [] });
  const [customerTypeFixing, setCustomerTypeFixing] = useState({});
  const [customerSpecialNeedsFixing, setCustomerSpecialNeedsFixing] = useState({});
  const [customerNoteFixing, setCustomerNoteFixing] = useState({});
  const [showImportAddressSuggestionsModal, setShowImportAddressSuggestionsModal] = useState(false);
  const [importAddressSuggestions, setImportAddressSuggestions] = useState([]);
  const [importAddressApplying, setImportAddressApplying] = useState(false);
  const scheduleImportProgressTimerRef = useRef(null);
  const routesOverviewProgressTimerRef = useRef(null);
  const exportProgressTimerRef = useRef(null);

  const stopExportProgressTimer = () => {
    if (exportProgressTimerRef.current) {
      clearInterval(exportProgressTimerRef.current);
      exportProgressTimerRef.current = null;
    }
  };

  const startExportRouteReportProgress = () => {
    stopExportProgressTimer();
    setIsExportingRouteReport(true);
    setExportRouteReportProgress(12);
    exportProgressTimerRef.current = setInterval(() => {
      setExportRouteReportProgress((prev) => (prev >= 90 ? 90 : prev + 5));
    }, 250);
  };

  const finishExportRouteReportProgress = () => {
    stopExportProgressTimer();
    setExportRouteReportProgress(100);
    setTimeout(() => {
      setIsExportingRouteReport(false);
      setExportRouteReportProgress(0);
    }, 250);
  };


  const params = new URLSearchParams(window.location.search);
  const scheduleDate = params.get('dateSchedule');

  const getDateString = (date) => {
    return ((date.getMonth() > 8) ? (date.getMonth() + 1) : ('0' + (date.getMonth() + 1))) + '/' + ((date.getDate() > 9) ? date.getDate() : ('0' + date.getDate())) + '/' + date.getFullYear()
  }

  /** Calendar-day future (not clock time). Avoids treating "today" as future when dateSelected is later today than new Date(). */
  const isFutureScheduleSelected = moment(dateSelected).startOf('day').isAfter(moment().startOf('day'));
  const isTodayView = getDateString(dateSelected) === getDateString(new Date());
  const hideExportAndViewByDateInScheduleEdit = isScheduleEditMode;
  const overviewScheduleEditMode = isScheduleEditMode && !isFutureScheduleSelected;
  const overviewFetchAfterCreate = isTodayView
    ? { fetchToday: true }
    : { fetchHistoryDate: getDateString(dateSelected) };

  const syncCustomerTypeForMismatch = async (issue) => {
    if (!issue?.customerId || !issue?.dbType || !issue?.mismatchedRoutes?.length) return;
    setCustomerTypeFixing((prev) => Object.assign({}, prev, { [issue.customerId]: true }));
    try {
      const routeMap = new Map();
      [...(tmrInboundRoutes || []), ...(tmrOutboundRoutes || [])].forEach((route) => {
        if (route?.id != null) routeMap.set(String(route.id), route);
        if (route?._id != null) routeMap.set(String(route._id), route);
      });
      const updatePromises = issue.mismatchedRoutes.map((routeMeta) => {
        const route = routeMap.get(String(routeMeta.routeId));
        if (!route) return Promise.resolve();
        const nextCustomerList = (route.route_customer_list || []).map((customer) => {
          if (customer?.customer_id !== issue.customerId) return customer;
          return Object.assign({}, customer, { customer_type: issue.dbType });
        });
        return TransRoutesService.updateRoute(route.id, Object.assign({}, route, { route_customer_list: nextCustomerList }));
      });
      await Promise.all(updatePromises);
      setSuccessMessage(`Synced member type for ${issue.customerName}.`);
      setTimeout(() => setSuccessMessage(undefined), 5000);
      dispatch(fetchAllTomorrowRoutes({dateText: moment(dateSelected).format('MM/DD/YYYY')}));
      await runCheckRoutes();
    } catch (error) {
      console.error('Error syncing customer type mismatch:', error);
      setErrorMessage(`Failed to sync member type for ${issue.customerName}.`);
      setTimeout(() => setErrorMessage(undefined), 5000);
    } finally {
      setCustomerTypeFixing((prev) => Object.assign({}, prev, { [issue.customerId]: false }));
    }
  };

  const syncCustomerSpecialNeedsForMismatch = async (issue) => {
    if (!issue?.customerId || !issue?.mismatchedRoutes?.length) return;
    setCustomerSpecialNeedsFixing((prev) => Object.assign({}, prev, { [issue.customerId]: true }));
    try {
      const routeMap = new Map();
      [...(tmrInboundRoutes || []), ...(tmrOutboundRoutes || [])].forEach((route) => {
        if (route?.id != null) routeMap.set(String(route.id), route);
        if (route?._id != null) routeMap.set(String(route._id), route);
      });
      const updatePromises = issue.mismatchedRoutes.map((routeMeta) => {
        const route = routeMap.get(String(routeMeta.routeId));
        if (!route) return Promise.resolve();
        const nextCustomerList = (route.route_customer_list || []).map((customer) => {
          if (customer?.customer_id !== issue.customerId) return customer;
          return Object.assign({}, customer, { customer_special_needs: issue.dbNoteToDriver || '' });
        });
        return TransRoutesService.updateRoute(route.id, Object.assign({}, route, { route_customer_list: nextCustomerList }));
      });
      await Promise.all(updatePromises);
      setSuccessMessage(`Synced note to driver for ${issue.customerName}.`);
      setTimeout(() => setSuccessMessage(undefined), 5000);
      dispatch(fetchAllTomorrowRoutes({dateText: moment(dateSelected).format('MM/DD/YYYY')}));
      await runCheckRoutes();
    } catch (error) {
      console.error('Error syncing note to driver mismatch:', error);
      setErrorMessage(`Failed to sync note to driver for ${issue.customerName}.`);
      setTimeout(() => setErrorMessage(undefined), 5000);
    } finally {
      setCustomerSpecialNeedsFixing((prev) => Object.assign({}, prev, { [issue.customerId]: false }));
    }
  };

  const syncCustomerNoteForMismatch = async (issue) => {
    if (!issue?.customerId || !issue?.mismatchedRoutes?.length) return;
    setCustomerNoteFixing((prev) => Object.assign({}, prev, { [issue.customerId]: true }));
    try {
      const routeMap = new Map();
      [...(tmrInboundRoutes || []), ...(tmrOutboundRoutes || [])].forEach((route) => {
        if (route?.id != null) routeMap.set(String(route.id), route);
        if (route?._id != null) routeMap.set(String(route._id), route);
      });
      const updatePromises = issue.mismatchedRoutes.map((routeMeta) => {
        const route = routeMap.get(String(routeMeta.routeId));
        if (!route) return Promise.resolve();
        const nextCustomerList = (route.route_customer_list || []).map((customer) => {
          if (customer?.customer_id !== issue.customerId) return customer;
          return Object.assign({}, customer, { customer_note: issue.dbNoteToDriver || '' });
        });
        return TransRoutesService.updateRoute(route.id, Object.assign({}, route, { route_customer_list: nextCustomerList }));
      });
      await Promise.all(updatePromises);
      setSuccessMessage(`Synced route note for ${issue.customerName}.`);
      setTimeout(() => setSuccessMessage(undefined), 5000);
      dispatch(fetchAllTomorrowRoutes({dateText: moment(dateSelected).format('MM/DD/YYYY')}));
      await runCheckRoutes();
    } catch (error) {
      console.error('Error syncing customer note mismatch:', error);
      setErrorMessage(`Failed to sync route note for ${issue.customerName}.`);
      setTimeout(() => setErrorMessage(undefined), 5000);
    } finally {
      setCustomerNoteFixing((prev) => Object.assign({}, prev, { [issue.customerId]: false }));
    }
  };

  const runCheckRoutes = async (opts) => {
    const inbound =
      opts?.inbound != null ? opts.inbound : (tmrInboundRoutes || []);
    const outbound =
      opts?.outbound != null ? opts.outbound : (tmrOutboundRoutes || []);
    const result = await runRouteChecks({
      inboundRoutes: inbound,
      outboundRoutes: outbound,
      customers,
      attendanceDate: dateSelected,
    });
    setCheckRoutesResult(result);
    setShowCheckRoutesModal(true);
  };

  const processRoutesForAbsentCustomers = useCallback((inboundRoutes, outboundRoutes) => {
    // Get customers who are absent in inbound routes
    const absentCustomerIds = new Set();
    
    inboundRoutes.forEach(inboundRoute => {
      inboundRoute.route_customer_list?.forEach(customer => {
        if (customer.customer_route_status === PERSONAL_ROUTE_STATUS.SCHEDULED_ABSENT || 
            customer.customer_route_status === PERSONAL_ROUTE_STATUS.UNEXPECTED_ABSENT) {
          absentCustomerIds.add(customer.customer_id);
        }
      });
    });

    // Remove absent customers from outbound routes for display
    const processedOutboundRoutes = outboundRoutes.map(outboundRoute => ({
      ...outboundRoute,
      route_customer_list: outboundRoute.route_customer_list?.filter(customer => 
        !absentCustomerIds.has(customer.customer_id)
      ) || []
    }));

    return processedOutboundRoutes;
  }, [])

  useEffect(() => {
    setPageLoading(true);
    if (scheduleDate) {
      const [year, month, day] = scheduleDate?.split('-').map(Number);
      setDateSelected(new Date(year, month-1, day));
    } else {
      setDateSelected(new Date())
    }
    const site = EventsService.site;
    Promise.all([
    DriverService.getAllActiveDrivers('driver', 'active').then((data) => {
      setDriverList(data.data);
      }),
      CustomerService.getAllCustomers().then((data) => setCustomers(data?.data)),
    CustomerService.getAvatar(`center_director_signature_site_${site}`).then(data => {
      if (data?.data) {
        setDirectorSignature(data?.data)
      }
      }).catch(() => {}),
    DailyRoutesTemplateService.getAll().then((response) => {
      setTemplates(response.data || []);
    }).catch(err => {
      console.error('Error fetching templates:', err);
      })
    ]).finally(() => {
      setPageLoading(false);
    });
  }, []);

  const routesSignatureDriverLookupKey = useMemo(() => {
    if (!routesForSignature?.length) return '';
    return routesForSignature.map((r) => r?.driver ?? '').sort().join('|');
  }, [routesForSignature]);

  useEffect(() => {
    if (!routesSignatureDriverLookupKey) {
      setExternalDriverNameById({});
      return;
    }
    let cancelled = false;
    EmployeeService.getExternalUserPermissionsList(EventsService.site)
      .then((res) => {
        if (!cancelled) setExternalDriverNameById(buildExternalDriverNameByIdMap(res?.data || []));
      })
      .catch(() => {
        if (!cancelled) setExternalDriverNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, [routesSignatureDriverLookupKey]);

  const getRouteDriverDisplayName = (driverId) => {
    if (driverId === undefined || driverId === null || driverId === '') return '';
    const fromFleet = drivers.find((d) => String(d.id) === String(driverId))?.name;
    if (fromFleet) return fromFleet;
    return externalDriverNameById[String(driverId)] || '';
  };

  useEffect(() => {
    let isCancelled = false;
    setRoutesSignatureLoading(true);

    const toDateTokens = (dateValue) => {
      const tokenSet = new Set();
      const localFormatted = moment(dateValue)?.format('MM/DD/YYYY');
      const utcFormatted = moment.utc(dateValue)?.format('MM/DD/YYYY');
      [localFormatted, utcFormatted].forEach((formatted) => {
        if (!formatted || formatted.toLowerCase().includes('invalid')) return;
        const [month, day] = formatted.split('/');
        if (month && day) {
          tokenSet.add(`${month}_${day}`);
        }
      });
      return Array.from(tokenSet).map((token) => token.split('_'));
    };
    const resolveRouteSignature = async (routeItem) => {
      const routeId = `${routeItem?.id ?? routeItem?._id ?? ''}`.trim();
      const requestRes = await SignatureRequestService.getAllSignatureRequests({ route_id: routeId }).catch(() => ({ data: [] }));
      const requestList = requestRes?.data || [];
      let driverIds = Array.from(new Set([
        routeItem?.driver,
        ...requestList.map((item) => item?.driver_id)
      ].filter(Boolean)));
      // Always include a "legacy" placeholder driver id to force the backend's loose GridFS fallback
      // (it matches routeId_*_MM_DD regardless of driver id). This helps when exact filename matching
      // fails due to legacy filename noise or driver id mismatches.
      driverIds = Array.from(new Set([...driverIds, 'legacy']));
      const datePairs = [];
      [routeItem?.schedule_date, dateSelected, ...requestList.map((item) => item?.route_date)].forEach((dateValue) => {
        toDateTokens(dateValue).forEach((pair) => datePairs.push(pair));
      });
      const uniqueDatePairs = Array.from(new Set(datePairs.map((pair) => `${pair[0]}_${pair[1]}`)))
        .map((token) => token.split('_'));
      for (const driverId of driverIds) {
        for (const [month, day] of uniqueDatePairs) {
          try {
            const result = await CustomerService.getAvatar(`${routeId}_${driverId}_${month}_${day}`);
            if (result?.data) {
              return result.data;
            }
        } catch (ex) {
            // Continue trying candidate keys.
          }
        }
      }
      return undefined;
    };

    TransRoutesService.getAll(moment(dateSelected)?.format('MM/DD/YYYY'))
      .then((data) => {
        const routesResults = data.data;
        const finalRoutes = routesResults.map(async (routeItem) => {
          const signature = await resolveRouteSignature(routeItem);
          return signature ? Object.assign({}, routeItem, {signature}) : routeItem;
        });
        return Promise.all(finalRoutes).then((finalRoutesData) => {
          if (!isCancelled) setRoutesForSignature(finalRoutesData);
        });
      })
      .catch((err) => {
        console.error('Failed to load routes signatures:', err);
        if (!isCancelled) setRoutesForSignature([]);
      })
      .finally(() => {
        if (!isCancelled) setRoutesSignatureLoading(false);
      });

    const selectedDateString = getDateString(dateSelected);
    if (selectedDateString === getDateString(new Date())) {
      dispatch(fetchAllRoutes());
    } else {
      if (isFutureScheduleSelected) {
        dispatch(fetchAllTomorrowRoutes({dateText: moment(dateSelected).format('MM/DD/YYYY')}));
      } else {
        dispatch(fetchAllHistoryRoutes({dateText: getDateString(dateSelected)}))
      }
    }
    return () => {
      isCancelled = true;
    };
  }, [dateSelected, isFutureScheduleSelected, dispatch]);

  useEffect(() => {
    const shouldShow = routesOverviewLoading && currentTab === 'allRoutesOverview';

    if (!shouldShow) {
      setRoutesOverviewProgress(0);
      if (routesOverviewProgressTimerRef.current) {
        clearInterval(routesOverviewProgressTimerRef.current);
        routesOverviewProgressTimerRef.current = null;
      }
      return;
    }

    // Fake progress that advances while the request is pending.
    setRoutesOverviewProgress(10);
    if (routesOverviewProgressTimerRef.current) {
      clearInterval(routesOverviewProgressTimerRef.current);
    }
    routesOverviewProgressTimerRef.current = setInterval(() => {
      setRoutesOverviewProgress((prev) => (prev >= 90 ? 90 : prev + 10));
    }, 250);

    return () => {
      if (routesOverviewProgressTimerRef.current) {
        clearInterval(routesOverviewProgressTimerRef.current);
        routesOverviewProgressTimerRef.current = null;
      }
    };
  }, [routesOverviewLoading, currentTab]);

  useEffect(() => {
    const selectedDateString = getDateString(dateSelected);
    if (!dateSelected || selectedDateString === getDateString(new Date())) {
      setRoutesForShowing(allRoutes);
      setRoutesInboundForShowing(inboundRoutes);
      setRoutesOutboundForShowing(processRoutesForAbsentCustomers(inboundRoutes, outboundRoutes));
    } else {
      if (isFutureScheduleSelected) {
        setRoutesForShowing(allTomorrowRoutes);
        setRoutesInboundForShowing(tmrInboundRoutes);
        setRoutesOutboundForShowing(processRoutesForAbsentCustomers(tmrInboundRoutes, tmrOutboundRoutes));
      } else {
        setRoutesForShowing(allHistoryRoutes);
        setRoutesOutboundForShowing(processRoutesForAbsentCustomers(historyInboundRoutes, historyOutboundRoutes));
        setRoutesInboundForShowing(historyInboundRoutes);
      }
    }
  }, [
    dateSelected,
    isFutureScheduleSelected,
    allRoutes,
    allHistoryRoutes,
    allTomorrowRoutes,
    inboundRoutes,
    outboundRoutes,
    tmrInboundRoutes,
    tmrOutboundRoutes,
    historyInboundRoutes,
    historyOutboundRoutes,
    processRoutesForAbsentCustomers,
  ]);

  useEffect(() => {
    return () => {
      stopExportProgressTimer();
    };
  }, []);

  const now = new Date();
  const tomorrow = new Date();

  const startScheduleImportProgress = (label) => {
    if (scheduleImportProgressTimerRef.current) {
      clearInterval(scheduleImportProgressTimerRef.current);
    }
    setScheduleImportLabel(label);
    setScheduleImportProgress(10);
    setIsScheduleImporting(true);
    scheduleImportProgressTimerRef.current = setInterval(() => {
      setScheduleImportProgress((prev) => (prev >= 90 ? 90 : prev + 5));
    }, 250);
  };

  const finishScheduleImportProgress = () => {
    if (scheduleImportProgressTimerRef.current) {
      clearInterval(scheduleImportProgressTimerRef.current);
      scheduleImportProgressTimerRef.current = null;
    }
    setScheduleImportProgress(100);
    setTimeout(() => {
      setIsScheduleImporting(false);
      setScheduleImportProgress(0);
      setScheduleImportLabel('');
    }, 400);
  };

  const refreshRoutesForCurrentDate = () => {
    const selectedDateString = getDateString(dateSelected);
    if (selectedDateString === getDateString(new Date())) {
      dispatch(fetchAllRoutes());
    } else if (isFutureScheduleSelected) {
      dispatch(fetchAllTomorrowRoutes({dateText: moment(dateSelected).format('MM/DD/YYYY')}));
    } else {
      dispatch(fetchAllHistoryRoutes({dateText: getDateString(dateSelected)}));
    }
  };

  const isOutboundRoute = (route) => `${route?.type || ""}`.trim().toLowerCase() === "outbound";

  const rebaseEstimatedStartTime = (sourceTime, targetDateText) => {
    if (!sourceTime) return null;
    const source = moment(sourceTime);
    if (!source.isValid()) return null;
    const target = moment(targetDateText, "MM/DD/YYYY", true);
    if (!target.isValid()) return source.toDate();
    target.set({
      hour: source.hour(),
      minute: source.minute(),
      second: source.second(),
      millisecond: 0,
    });
    return target.toDate();
  };

  const buildImportedRouteData = (draftRoute, targetDateText, customerById = null) => {
    return {
      name: draftRoute?.name,
      vehicle: draftRoute?.vehicle,
      driver: draftRoute?.driver,
      type: draftRoute?.type,
      schedule_date: targetDateText,
      start_mileage: draftRoute?.start_mileage || vehicles.find((vehicle) => vehicle.id === draftRoute?.vehicle)?.mileage,
      end_mileage: draftRoute?.end_mileage,
      status: [],
      start_time: null,
      end_time: null,
      estimated_start_time: isOutboundRoute(draftRoute)
        ? rebaseEstimatedStartTime(draftRoute?.estimated_start_time, targetDateText)
        : null,
      checklist_result: draftRoute?.checklist_result || [],
      route_customer_list: (draftRoute.route_customer_list || []).map((customer) => {
        const synced = applyLatestCustomerInfoToRouteCustomer(customer, customerById);
        if (hasAddressSlot(synced)) {
          return {
            ...synced,
            customer_enter_center_time: null,
            customer_leave_center_time: null,
            customer_pickup_time: null,
            customer_dropoff_time: null,
            customer_estimated_pickup_time: null,
            customer_estimated_dropoff_time: null,
            customer_route_status: null,
            customer_address_override: null,
            customer_address_slot: synced.customer_address_slot,
            customer_route_base_address: "",
            customer_address: "",
          };
        }
        const addr = (synced.customer_address || '').toString().trim();
        const base =
          (synced.customer_route_base_address || '').toString().trim() || addr;
        return {
          ...synced,
          customer_enter_center_time: null,
          customer_leave_center_time: null,
          customer_pickup_time: null,
          customer_dropoff_time: null,
          customer_estimated_pickup_time: null,
          customer_estimated_dropoff_time: null,
          customer_route_status: null,
          customer_address_override: null,
          customer_route_base_address: base || undefined,
          customer_address: base || synced.customer_address,
        };
      })
    };
  };

  const loadCustomerByIdMapForImport = async () => {
    try {
      const res = await CustomerService.getAllCustomers();
      const list = res?.data || [];
      setCustomers(list);
      const map = new Map();
      list.forEach((customer) => {
        if (customer?.id != null) map.set(String(customer.id), customer);
      });
      return map;
    } catch (error) {
      console.error('Failed to load customers for import sync:', error);
      const map = new Map();
      (customers || []).forEach((customer) => {
        if (customer?.id != null) map.set(String(customer.id), customer);
      });
      return map;
    }
  };

  const showPostImportAddressSuggestionsIfNeeded = async (customerById, targetDateText) => {
    try {
      const { data: routes } = await TransRoutesService.getAll(targetDateText);
      const customersList = Array.from(customerById?.values?.() || []);
      const allRoutes = routes || [];
      const result = await runRouteChecks({
        inboundRoutes: allRoutes.filter((route) => route?.type === 'inbound'),
        outboundRoutes: allRoutes.filter((route) => route?.type === 'outbound'),
        customers: customersList,
        attendanceDate: dateSelected,
      });
      const suggestions = (result?.addressMismatch || []).filter(
        (issue) => `${issue?.suggestedAddress || ''}`.trim() !== ''
      );
      if (suggestions.length > 0) {
        setImportAddressSuggestions(suggestions);
        setShowImportAddressSuggestionsModal(true);
      }
    } catch (error) {
      console.error('Failed to check address suggestions after import:', error);
    }
  };

  const ignoreImportAddressSuggestions = () => {
    setShowImportAddressSuggestionsModal(false);
    setImportAddressSuggestions([]);
  };

  const applyImportAddressSuggestions = async () => {
    if (!importAddressSuggestions.length) {
      ignoreImportAddressSuggestions();
      return;
    }
    setImportAddressApplying(true);
    try {
      const targetDateText = getDateString(dateSelected);
      const { data: routes } = await TransRoutesService.getAll(targetDateText);
      const routeMap = new Map();
      (routes || []).forEach((route) => {
        if (route?.id != null) routeMap.set(String(route.id), route);
        if (route?._id != null) routeMap.set(String(route._id), route);
      });

      const patchesByRouteId = new Map();
      importAddressSuggestions.forEach((issue) => {
        const suggested = `${issue?.suggestedAddress || ''}`.trim();
        if (!suggested || issue?.customerId == null) return;
        (issue.mismatchedRoutes || []).forEach((routeMeta) => {
          const routeId = String(routeMeta?.routeId || '');
          if (!routeId) return;
          if (!patchesByRouteId.has(routeId)) patchesByRouteId.set(routeId, new Map());
          patchesByRouteId.get(routeId).set(String(issue.customerId), suggested);
        });
      });

      const updatePromises = [];
      patchesByRouteId.forEach((customerPatches, routeId) => {
        const route = routeMap.get(routeId);
        if (!route) return;
        const nextCustomerList = (route.route_customer_list || []).map((customer) => {
          const suggested = customerPatches.get(String(customer?.customer_id));
          if (!suggested) return customer;
          return Object.assign({}, customer, {
            customer_address: suggested,
            customer_route_base_address: suggested,
            customer_address_override: null,
          });
        });
        updatePromises.push(
          TransRoutesService.updateRoute(
            route.id,
            Object.assign({}, route, { route_customer_list: nextCustomerList })
          )
        );
      });

      await Promise.all(updatePromises);
      const appliedCount = importAddressSuggestions.length;
      refreshRoutesForCurrentDate();
      ignoreImportAddressSuggestions();
      setSuccessMessage(`Applied ${appliedCount} suggested address fix(es).`);
      setTimeout(() => setSuccessMessage(undefined), 5000);
    } catch (error) {
      console.error('Failed to apply suggested address fixes:', error);
      setErrorMessage(error?.message || 'Failed to apply suggested address fixes.');
      setTimeout(() => setErrorMessage(undefined), 5000);
    } finally {
      setImportAddressApplying(false);
    }
  };

  const importFromDate = async () => {
    if (!importSourceDate) {
      window.alert('Please select a source date.');
      return;
    }
    startScheduleImportProgress('Importing schedule from selected date...');
    try {
      const sourceDateText = getDateString(importSourceDate);
      const targetDateText = getDateString(dateSelected);
      const [customerById, { data: sourceRoutes }, { data: existingRoutes }] = await Promise.all([
        loadCustomerByIdMapForImport(),
        TransRoutesService.getAll(sourceDateText),
        TransRoutesService.getAll(targetDateText)
      ]);

      let skippedCount = 0;
      const createPromises = [];
      for (const draftRoute of (sourceRoutes || [])) {
        if (!draftRoute?.type || `${draftRoute?.type}`.trim() === '') continue;
        const existed = (existingRoutes || []).find((r) => r.name === draftRoute.name && r.type === draftRoute.type);
        if (existed) {
          skippedCount++;
          continue;
        }
        const routeData = buildImportedRouteData(draftRoute, targetDateText, customerById);
        createPromises.push(TransRoutesService.createNewRoute(routeData));
      }
      await Promise.all(createPromises);
      refreshRoutesForCurrentDate();
      setShowImportDateDropdown(false);
      setSuccessMessage(`Imported ${createPromises.length} route(s) from ${sourceDateText}.`);
      setTimeout(() => setSuccessMessage(undefined), 5000);
      if (skippedCount > 0) {
        window.alert(`${skippedCount} route(s) already existed on target date and were skipped.`);
      }
      if (createPromises.length > 0) {
        await showPostImportAddressSuggestionsIfNeeded(customerById, targetDateText);
      }
    } catch (error) {
      console.error('Failed to import routes from date:', error);
      setErrorMessage(error?.message || 'Failed to import routes from date');
      setTimeout(() => setErrorMessage(undefined), 5000);
    } finally {
      finishScheduleImportProgress();
    }
  };

  const importFromTemplate = async () => {
    if (!importTemplateId) {
      window.alert('Please select a template.');
      return;
    }
    const selectedTemplate = templates.find((t) => t.id === importTemplateId);
    if (!selectedTemplate) {
      window.alert('Template not found.');
      return;
    }
    startScheduleImportProgress('Copying routes from template...');
    try {
      const targetDateText = getDateString(dateSelected);
      const templateDateText = `${selectedTemplate.template_date || ""}`.trim();
      const [customerById, { data: existingRoutes }, sourceDateResult] = await Promise.all([
        loadCustomerByIdMapForImport(),
        TransRoutesService.getAll(targetDateText),
        templateDateText
          ? TransRoutesService.getAll(templateDateText).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      const outboundStartTimeByName = new Map();
      (sourceDateResult?.data || []).forEach((route) => {
        if (!isOutboundRoute(route) || !route?.estimated_start_time) return;
        outboundStartTimeByName.set(`${route.name || ""}`, route.estimated_start_time);
      });
      let skippedCount = 0;
      const createPromises = [];
      for (const route of (selectedTemplate.routes || [])) {
        if (!route?.type || `${route?.type}`.trim() === '') continue;
        const existed = (existingRoutes || []).find((r) => r.name === route.name && r.type === route.type);
        if (existed) {
          skippedCount++;
          continue;
        }
        const estimatedStartTime = route?.estimated_start_time
          || (isOutboundRoute(route) ? outboundStartTimeByName.get(`${route.name || ""}`) : null);
        const routeData = buildImportedRouteData(
          { ...route, estimated_start_time: estimatedStartTime || null },
          targetDateText,
          customerById
        );
        createPromises.push(TransRoutesService.createNewRoute(routeData));
      }
      await Promise.all(createPromises);
      refreshRoutesForCurrentDate();
      setShowImportTemplateDropdown(false);
      setImportTemplateId('');
      setSuccessMessage(`Imported ${createPromises.length} route(s) from template "${selectedTemplate.name}".`);
      setTimeout(() => setSuccessMessage(undefined), 5000);
      if (skippedCount > 0) {
        window.alert(`${skippedCount} route(s) already existed on target date and were skipped.`);
      }
      if (createPromises.length > 0) {
        await showPostImportAddressSuggestionsIfNeeded(customerById, targetDateText);
      }
    } catch (error) {
      console.error('Failed to import routes from template:', error);
      setErrorMessage(error?.message || 'Failed to import routes from template');
      setTimeout(() => setErrorMessage(undefined), 5000);
    } finally {
      finishScheduleImportProgress();
    }
  };
  tomorrow.setDate(now.getDate() + 1);
  
  const directToSchedule = () => {
    setDateSelected(tomorrow);
  }
  const generateRouteReport = async () => {
    const targetDate = dateSelected || now;
    if (isExportingRouteReport) return;
    startExportRouteReportProgress();
    try {
      const selectedDateText = getDateString(targetDate);
      const response = await ReportService.exportRouteReportZip(selectedDateText);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadName = getZipFilenameFromHeaders(response?.headers);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export route report:', error);
      const message = await getErrorMessageFromBlob(error?.response?.data);
      window.alert(message);
    } finally {
      finishExportRouteReportProgress();
    }
  };

  const generateDietaryRecordsExcel = async () => {
    const targetDate = dateSelected || now;
    if (isExportingRouteReport) return;
    startExportRouteReportProgress();
    try {
      const selectedDateText = getDateString(targetDate);
      const response = await ReportService.exportDietaryRecordsExcel(selectedDateText);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Dietary_Records_${selectedDateText.replace(/\//g, '-')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export dietary records:', error);
      const message = await getErrorMessageFromBlob(error?.response?.data);
      window.alert(message);
    } finally {
      finishExportRouteReportProgress();
    }
  };

  const exportDepartureBoardExcel = async () => {
    if (isExportingRouteReport) return;
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (departure board)",
      entityType: "export",
      entityId: "trans-routes-departure-board",
    }).catch(() => {});
    startExportRouteReportProgress();
    try {
      await downloadDepartureBoardExcel({
        outboundRoutes: routesOutboundForShowing,
        customers,
        vehicles,
        getDriverName: getRouteDriverDisplayName,
        scheduleDate: dateSelected,
      });
    } catch (e) {
      console.error("Failed to export departure board:", e);
      window.alert("Could not create Departure Board Excel file.");
    } finally {
      finishExportRouteReportProgress();
    }
  };

  /** All Routes Status tab — same Excel as "Generate Inbound Participants Report" (moved to toolbar Export). */
  const exportInboundParticipantsReportFromStatusTab = async () => {
    if (isExportingRouteReport) return;
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (inbound participants report)",
      entityType: "export",
      entityId: "trans-routes-inbound-participants",
    }).catch(() => {});
    startExportRouteReportProgress();
    try {
      const matrix = TransRoutesService.buildInboundParticipantsReportMatrix(
        routesForShowing,
        vehicles
      );
      await downloadXlsxFromMatrix(
        matrix,
        'Route Inbound Customers Show-up Report',
        'Report'
      );
    } catch (e) {
      console.error(e);
      window.alert('Could not create Excel file.');
    } finally {
      finishExportRouteReportProgress();
    }
  };

  const goToHistoryPage = () => {
    navigate('/trans-routes/history');
  }

  const createDriver = () => {
    navigate('/employees?redirect=schedule&type=driver');
  }
  // const goToSignature = () => {
  //   navigate('/trans-routes/route-signature');
  // }
  const createNewRoute = () => {
    navigate('/trans-routes/create')
  }
  const goToCreateRoute = (type) => {
    navigate(`/trans-routes/create?type=${type}&date=${dateSelected? moment(dateSelected).format('YYYY-MM-DD'): 'tomorrow'}`);
  }
  const formatDateYYYYMMDD = (d) => {
    if (!d) return '';
    const dateObj = new Date(d);
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const goToRouteDetails = (routeId) => {
    if (!routeId) return;
    const qs = new URLSearchParams();
    const selectedDateString = getDateString(dateSelected);
    if (selectedDateString !== getDateString(new Date())) {
      qs.set('dateSchedule', formatDateYYYYMMDD(dateSelected));
    }
    if (isScheduleEditMode) qs.set('scheduleEdit', '1');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    navigate(`/trans-routes/${routeId}${suffix}`);
  };
  const changeTab = (k) => {
    setCurrentTab(k);
    setKeyword('');
    setSorting({key: '', order: ''});
    setOriginDateSelected(undefined);
    setTargetedDateSelected(undefined);
    if (k !== 'allRoutesOverview') {
      const p = new URLSearchParams(window.location.search);
      p.delete('scheduleEdit');
      const search = p.toString();
      navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    }
    // setSelectedDriver(undefined);
  }

  const cleanupSchedule = async () => {
    const routesToDisable = (routesForShowing || []).filter((route) => {
      if (deleteRouteType === 'inbound') return route?.type === 'inbound';
      if (deleteRouteType === 'outbound') return route?.type === 'outbound';
      return true;
    });

    const disableRequests = routesToDisable
      .map((route) => route?.id || route?._id)
      .filter(Boolean)
      .map((routeId) => TransRoutesService.updateRoute(routeId, { status: ['disabled'] }));

    await Promise.allSettled(disableRequests);
      closeDeleteModal();

    const selectedDateString = getDateString(dateSelected);
    if (selectedDateString === getDateString(new Date())) {
      dispatch(fetchAllRoutes());
    } else if (isFutureScheduleSelected) {
      dispatch(fetchAllTomorrowRoutes({ dateText: moment(dateSelected).format('MM/DD/YYYY') }));
    } else {
      dispatch(fetchAllHistoryRoutes({ dateText: getDateString(dateSelected) }));
    }
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteRouteType('');
  }

  const triggerShowDeleteModal = (routeType = '') => {
    setDeleteRouteType(routeType);
    setShowDeleteModal(true);
  }

  const startToScheduleDate = (v) => {
    const p = new URLSearchParams(window.location.search);
    p.set('dateSchedule', moment(v).format('YYYY-MM-DD'));
    p.delete('scheduleEdit');
    const search = p.toString();
    navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    dispatch(fetchAllTomorrowRoutes({dateText: moment(v).format('MM/DD/YYYY')}));
  } 

  const validateSchedule = () => {
    const inboundCustomersRouteMap = {};
    let success = true;
    for (const inboundRoute of tmrInboundRoutes) {
      for (const inboundCustomer of inboundRoute.route_customer_list) {
        if (Object.keys(inboundCustomersRouteMap).includes(inboundCustomer.customer_id) && inboundCustomer.customer_route_status !== PERSONAL_ROUTE_STATUS.DISABLED) {
          setSuccessMessage(undefined);
          success = false;
          setErrorMessage(`Error: Customer ${inboundCustomer.customer_name} was scheduled in both inbound ${inboundRoute.name} and ${inboundCustomersRouteMap[inboundCustomer.customer_id].name}.`)
          break;
        } else {
          if (inboundCustomer.customer_route_status !== PERSONAL_ROUTE_STATUS.DISABLED) {
            inboundCustomersRouteMap[inboundCustomer.customer_id] = inboundRoute;
          }
          
        }
      }
    }
    const outboundCustomersRouteMap = {};
    for (const outboundRoute of tmrOutboundRoutes) {
      for (const outboundCustomer of outboundRoute.route_customer_list) {
        if (Object.keys(outboundCustomersRouteMap).includes(outboundCustomer.customer_id) && outboundCustomer.customer_route_status !== PERSONAL_ROUTE_STATUS.DISABLED) {
          setSuccessMessage(undefined);
          success = false;
          setErrorMessage(`Error: Customer ${outboundCustomer.customer_name} was scheduled in both outbound ${outboundRoute.name} and ${outboundCustomersRouteMap[outboundCustomer.customer_id].name}.`)
          break;
        } else {
          if (outboundCustomer.customer_route_status !== PERSONAL_ROUTE_STATUS.DISABLED) {
            outboundCustomersRouteMap[outboundCustomer.customer_id] = outboundRoute;
          }
          
        }
      }
    }
    if (success) {
      setErrorMessage(undefined);
      setSuccessMessage(`Routes Schedule Validate Successfully`);
    }
  }

  const copyTodayRoutesOver = () => {
    setShowCopyTodayLoading(true);
    setIsLoading(true);
    let count = 0;
    TransRoutesService.getAll(((now.getMonth() > 8) ? (now.getMonth() + 1) : ('0' + (now.getMonth() + 1))) + '/' + ((now.getDate() > 9) ? now.getDate() : ('0' + now.getDate())) + '/' + now.getFullYear()).then(existingRoutes => {
      for (const draftRoute of allRoutes) {
        if (!draftRoute?.type || `${draftRoute?.type}`.trim() === '') continue;
        const existed = existingRoutes?.data?.find(r => r.name === draftRoute.name);
        if (draftRoute && !existed) {
          const data = Object.assign({}, {
            name: draftRoute.name,
            schedule_date: ((now.getMonth() > 8) ? (now.getMonth() + 1) : ('0' + (now.getMonth() + 1))) + '/' + ((now.getDate() > 9) ? now.getDate() : ('0' + now.getDate())) + '/' + now.getFullYear(),
            vehicle: draftRoute.vehicle,
            driver: draftRoute.driver,
            type: draftRoute.type,
            start_mileage: vehicles.find((vehicle) => vehicle.id === draftRoute.vehicle)?.mileage,
            route_customer_list: draftRoute.route_customer_list?.map((customer) => {
              const addr = (customer.customer_address || '').toString().trim();
              const base =
                (customer.customer_route_base_address || '').toString().trim() || addr;
              return Object.assign({}, customer, {
                customer_enter_center_time: null,
                customer_leave_center_time: null,
                customer_pickup_time: null,
                customer_dropoff_time: null,
                customer_estimated_pickup_time: null,
                customer_estimated_dropoff_time: null,
                customer_route_status: null,
                customer_address_override: null,
                customer_route_base_address: base || undefined,
                customer_address: base || customer.customer_address,
              });
            }),
          });
          dispatch(createRoute({ data }));
        } else {
          if (existed) {
            count++;
          }
        }
      }
      setTimeout(() => {
        dispatch(fetchAllTomorrowRoutes({}));
        setShowCopyTodayLoading(false);
        setCopyDisabled(true);
        setIsLoading(false);
        setSuccessMessage('Routes Copied Successfully, please do not click the button again!');
        if (count > 0) {
          window.alert(`${count} routes has existed in selected date and is not copied again!`)
        }
      }, 2000);
    })
  }

  const copyDateRoutesOver = (withTargetDate) => {
    const dateOrigin = new Date(originDateSelected);
    const newScheduleDate = withTargetDate ? new Date(targetedDateSelected) : now;
    if (withTargetDate) {
      setShowCopyDateTargetLoading(true);
    } else {
      setShowCopyDateLoading(true);
    }

    setIsLoading(true);
    let count = 0;
    TransRoutesService.getAll(getDateString(dateOrigin)).then(({data: allHistoryRoutes}) => {
      TransRoutesService.getAll(((newScheduleDate.getMonth() > 8) ? (newScheduleDate.getMonth() + 1) : ('0' + (newScheduleDate.getMonth() + 1))) + '/' + ((newScheduleDate.getDate() > 9) ? newScheduleDate.getDate() : ('0' + newScheduleDate.getDate())) + '/' + newScheduleDate.getFullYear()).then(existingRoutes => {
        for (const draftRoute of allHistoryRoutes) {
          if (!draftRoute?.type || `${draftRoute?.type}`.trim() === '') continue;
          const existed = existingRoutes?.data?.find((r) => r. name === draftRoute.name);
          if (draftRoute && !existed) {
            const data = Object.assign({}, {
              name: draftRoute.name,
              schedule_date: ((newScheduleDate.getMonth() > 8) ? (newScheduleDate.getMonth() + 1) : ('0' + (newScheduleDate.getMonth() + 1))) + '/' + ((newScheduleDate.getDate() > 9) ? newScheduleDate.getDate() : ('0' + newScheduleDate.getDate())) + '/' + newScheduleDate.getFullYear(),
              vehicle: draftRoute.vehicle,
              driver: draftRoute.driver,
              type: draftRoute.type,
              start_mileage: vehicles.find((vehicle) => vehicle.id === draftRoute.vehicle)?.mileage,
              route_customer_list: draftRoute.route_customer_list?.map((customer) => {
                const addr = (customer.customer_address || '').toString().trim();
                const base =
                  (customer.customer_route_base_address || '').toString().trim() || addr;
                return Object.assign({}, customer, {
                  customer_enter_center_time: null,
                  customer_leave_center_time: null,
                  customer_pickup_time: null,
                  customer_dropoff_time: null,
                  customer_estimated_pickup_time: null,
                  customer_estimated_dropoff_time: null,
                  customer_route_status: null,
                  customer_address_override: null,
                  customer_route_base_address: base || undefined,
                  customer_address: base || customer.customer_address,
                });
              }),
            });
            dispatch(createRoute({ data }));
          } else {
            if (existed) {
              count++;
            }
          }
        }
        setTimeout(() => {
          // dispatch(fetchAllTomorrowRoutes({}));
          startToScheduleDate(withTargetDate ? targetedDateSelected : now);
          if (withTargetDate) {
            setShowCopyDateTargetLoading(false);
          } else {
            setShowCopyDateLoading(false);
          }
          setIsLoading(false);
          // setCopyDateDisabled(true);
          setSuccessMessage('Routes Copied Successfully, please do not click the button again!');
          setDateSelected(targetedDateSelected);
          setOriginDateSelected(undefined);
          setTargetedDateSelected(undefined);
          if (count > 0) {
            window.alert(`${count} routes has existed on selected date and are not copied again.`)
          }
        }, 2000);
      });
    });
  }


  const uploadDirectorSignature = () => {
    const formData = new FormData();
    const site = EventsService.site;
  	formData.append("file", selectedFile);
    if (selectedFile) {
      if (directorSignature) {
        CustomerService.deleteFile({'name': `center_director_signature_site_${site}`}).then(() => {
          CustomerService.uploadAvatar(`center_director_signature_site_${site}`, formData).then(() => {
            CustomerService.getAvatar(`center_director_signature_site_${site}`).then(data => {
              if (data?.data) {
                setDirectorSignature(data?.data)
              }
            });
          })
        })
      } else {
        CustomerService.uploadAvatar(`center_director_signature_site_${site}`, formData).then(() => {
          CustomerService.getAvatar(`center_director_signature_site_${site}`).then(data => {
            if (data?.data) {
              setDirectorSignature(data?.data)
            }
          });
        })
      }
    }
  }

  const getAllUniqueCustomers = (routes) => {
    let result = [];
    for (const route of routes) {
      const customerList = route.route_customer_list.map(item => Object.assign({}, item, {routeType: route.type, routeId: route.id, route: route, customer_status_inbound: route.type === 'inbound' && item.customer_route_status, customer_status_outbound: route.type === 'outbound' && item.customer_route_status,  inbound: route.type === 'inbound' && route, outbound: route.type === 'outbound' && route}))
      for (const customer of customerList) {
        const existItem = result.find((item => (item.customer_id === customer.customer_id) || (item?.customer_name?.replaceAll(' ', '')?.toLowerCase() === customer?.customer_name?.replaceAll(' ', '')?.toLowerCase()) ));
        if (existItem) {
          result = result.filter(item => item !== existItem);
          const newItem = Object.assign({}, existItem, {
            customer_enter_center_time: existItem?.customer_enter_center_time || customer?.customer_enter_center_time,
            customer_leave_center_time: existItem?.customer_leave_center_time || customer?.customer_leave_center_time,
            customer_pickup_time: existItem?.customer_pickup_time || customer?.customer_pickup_time,
            customer_dropoff_time: existItem?.customer_dropoff_time || customer?.customer_dropoff_time,
            inbound: existItem?.inbound || customer?.inbound,
            outbound: existItem?.outbound || customer?.outbound,
            customer_status_inbound: existItem?.customer_status_inbound || customer?.customer_status_inbound,
            customer_status_outbound: existItem?.customer_status_outbound || customer?.customer_status_outbound
          })
          result.push(newItem);
        } else {
          result.push(customer);
        }
      }
    }
    return result.sort((a, b) => {
      if (a.customer_name < b.customer_name) {
        return -1;
      } else {
        return 1;
      }
    });
  }

  const syncCustomersInfo = () => {
    setShowSyncCustomersLoading(true);
    CustomerService.getAllCustomers().then(data => {
      const customers = data.data;
      const customersMap = new Map();
      for (const customer of customers) {
        customersMap.set(customer.id, {
          customer_name: `${customer.name} ${customer.name_cn?.length > 0 ? `(${customer.name_cn})` : ``}`,
          customer_type: customer.type,
          customer_pickup_status: customer.pickup_status,
          customer_note: customer.note,
          customer_special_needs: customer.special_needs,
          customer_phone: customer.phone || customer.mobile_phone || customer.home_phone,
          customer_table_id: customer.table_id,
          customer_language: customer.language
        })
      };
      // Batch all route updates, then refetch once after all complete
      const updatePromises = allRoutes.map(route => {
        const customerList = route.route_customer_list;
        const newCustomerList = customerList.map((customerItem) => Object.assign({}, customerItem, customersMap.get(customerItem.customer_id)));
        const newRouteObject = Object.assign({}, route, {route_customer_list: newCustomerList});
        return TransRoutesService.updateRoute(route.id, newRouteObject);
      });
      Promise.all(updatePromises)
        .then(() => {
          dispatch(fetchAllRoutes());
        })
        .catch(err => {
          console.error('Error syncing customer info:', err);
        })
        .finally(() => {
          setShowSyncCustomersLoading(false);
        });
    })
  }

  const cleanFilterAndClose = () => {
    setSelectedDriver(null);
    // setDateSelected(new Date())
    setShowFilterDropdown(false);
    setShowDateDropdown(false);
    setCustomerTableId('');
    setStatusFilter('');
    setCustomerNameFilter('');
    setCustomerTypeFilter('');
    setKeyword('');
    setRouteTypeFilter('');
    setSignatureRouteTypeFilter('');
    setSignatureHasSignatureFilter('');
    setShowFilterReportDropdown(false);
  }

  const cleanDate = () => {
    setDateSelected(new Date());
    setShowDateDropdown(false);
  }

  const FilterAndClose = () => {
    setShowFilterDropdown(false);
    setShowDateDropdown(false);
    setShowFilterReportDropdown(false);
  }

  const getSortingImg = (key) => {
    return sorting.key === key ? (sorting.order === 'asc' ? 'up_arrow' : 'down_arrow') : 'default';
  }

  const sortTableWithField = (key) => {
    let newSorting = {
      key,
      order: 'asc',
    }
    
    if (sorting.key === key && sorting.order === 'asc') {
      newSorting = {...newSorting, order: 'desc'};
    }
    setSorting(newSorting);
  }

  const pickOriginDateSelected = (v) => {
    if (v) {
      setOriginDateSelected(v);
      setDateSelected(v);
      const p = new URLSearchParams(window.location.search);
      p.set('dateSchedule', moment(v).format('YYYY-MM-DD'));
      p.delete('scheduleEdit');
      const search = p.toString();
      navigate(`${window.location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    }
    setShowOriginDateDropdown(false);
  }

  const columns = [
    {
      key: 'name',
      label: 'Route Name'
    },
    {
      key: 'driver',
      label: 'Driver'
    },
    {
      key: 'estimated_start_time',
      label: 'Estimated Start Time'
    },
    {
      key: 'end_time',
      label: 'Route End Time'
    },
    {
      key: 'type',
      label: 'Route Type'
    },
  ];

  const customMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <h6>Filter By</h6>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Driver</div>
              <select value={selectedDriver || ''} onChange={e => setSelectedDriver(e.target.value || null)}>
                <option value=""></option>
                {sortDriversByLastName(driverList).map((driver) => <option key={driver?.id} value={driver?.id}>{formatDriverDropdownLabel(driver)}</option>)}
              </select>
            </div>
            <div className="me-4">
              <div className="field-label">Route Type</div>
              <select value={signatureRouteTypeFilter} onChange={(e) => setSignatureRouteTypeFilter(e.target.value)}>
                <option value=""></option>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </div>
            <div className="me-4">
              <div className="field-label">Has Signature</div>
              <select value={signatureHasSignatureFilter} onChange={(e) => setSignatureHasSignatureFilter(e.target.value)}>
                <option value=""></option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => cleanFilterAndClose()}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={() => FilterAndClose()}> Filter </button>
            </div>
          </div>
        </div>
      );
    },
  );

  const customReportFilterMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <h6>Filter By</h6>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Participate Status</div>
              <select
                value={statusFilter}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {
                  [['', {text: ''}], ...Object.entries(PERSONAL_ROUTE_STATUS_TEXT)].map(([key, {text}]) => (
                    <option key={key} value={key}>{text}</option>
                  ))
                }
              </select>
            </div>
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Participant Type</div>
              <select
                value={customerTypeFilter}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setCustomerTypeFilter(e.target.value)}
              >
                {
                  [['', ''], ...Object.entries(CUSTOMER_TYPE_TEXT).filter(([key]) => !HIDDEN_CUSTOMER_TYPE_FILTER_VALUES.includes(key))].map(([key, text]) => (
                    <option key={key} value={key}>{text}</option>
                  ))
                }
              </select>
            </div>
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Routes Type</div>
              <select
                value={routeTypeFilter}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setRouteTypeFilter(e.target.value)}
              >
                <option value=""></option>
                <option value="inbound">inbound</option>
                <option value="outbound">outbound</option>
              </select>
            </div>
          </div>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Participant Name</div>
              <input type="text" value={customerNameFilter} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onChange={(e) => setCustomerNameFilter(e.target.value)} />
            </div>
            <div className="me-4">
              <div className="field-label">Table Id</div>
              <input type="text" value={customerTableId} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} onChange={(e) => setCustomerTableId(e.target.value)} />
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => cleanFilterAndClose()}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={() => FilterAndClose()}> Filter </button>
            </div>
          </div>
        </div>
      );
    },
  );

  const customMenuOriginDate = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Select Date to Start</div>
              <DatePicker selected={originDateSelected} onChange={(v) => pickOriginDateSelected(v)} shouldCloseOnSelect />
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => setShowOriginDateDropdown(false)}> Close </button>
            </div>
          </div>
        </div>
      );
    },
  );

  const customMenuImportFromDate = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Select Source Date (Past 14 Days)</div>
              <DatePicker
                selected={importSourceDate}
                onChange={(v) => setImportSourceDate(v)}
                minDate={moment().subtract(14, 'days').toDate()}
                maxDate={new Date()}
                shouldCloseOnSelect
              />
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => setShowImportDateDropdown(false)}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={importFromDate}> Import </button>
            </div>
          </div>
        </div>
      );
    },
  );

  const customMenuImportFromTemplate = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Select Saved Template</div>
              <select
                className="form-select"
                value={importTemplateId}
                onChange={(e) => setImportTemplateId(e.target.value)}
              >
                <option value="">Choose a template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} - {template.template_date}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => setShowImportTemplateDropdown(false)}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={importFromTemplate}> Copy </button>
            </div>
          </div>
        </div>
      );
    },
  );

  const customMenuTargetDate = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
              <div className="field-label">Select Date to Start</div>
              <DatePicker selected={targetedDateSelected} onChange={(v) => setTargetedDateSelected(v)} shouldCloseOnSelect />
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <button className="btn btn-default btn-sm float-right" onClick={() => {setTargetedDateSelected(undefined); setShowTargetDateDropdown(false)}}> Cancel </button>
              <button className="btn btn-primary btn-sm float-right" onClick={() => {copyDateRoutesOver(true); setShowTargetDateDropdown(false)}}> Start to Copy </button>
            </div>
          </div>
        </div>
      );
    },
  );



  // Save template modal handlers
  const openSaveTemplateModal = () => {
    setShowSaveTemplateModal(true);
    setTemplateName('');
  };

  const closeSaveTemplateModal = () => {
    setShowSaveTemplateModal(false);
    setTemplateName('');
  };

  const saveRoutesAsTemplate = () => {
    if (!templateName || templateName.trim() === '') {
      alert('Please enter a template name');
      return;
    }

    setSavingTemplate(true);

    // Get the current date string in MM/DD/YYYY format
    const templateDate = getDateString(dateSelected);

    // Get current user from localStorage
    const currentUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user'))?.name : '';

    // Clean the routes data - remove all date fields and status
    const cleanedRoutes = routesForShowing.map(route => {
      // Clean customer list - set all date fields to null and reset dynamic fields to undefined
      const cleanedCustomerList = stabilizeRouteCustomerAddressList(
        route.route_customer_list || [],
        customers,
        route.route_customer_list || []
      ).map(customer => ({
        ...customer,
        customer_enter_center_time: null,
        customer_leave_center_time: null,
        customer_pickup_time: null,
        customer_dropoff_time: null,
        customer_route_status: null,
        // customer_estimated_pickup_time: undefined,
        // customer_estimated_dropoff_time: undefined,
        // customer_pickup_status: undefined,
        // customer_route_status: undefined,
        // customer_transfer_to_route: undefined,
        // customer_address_override: undefined
      }));

      // Return cleaned route
      return {
        name: route.name,
        schedule_date: route.schedule_date,
        vehicle: route.vehicle,
        status: [], // Empty array
        driver: route.driver,
        type: route.type,
        start_mileage: route.start_mileage,
        end_mileage: route.end_mileage,
        start_time: null,
        end_time: null,
        estimated_start_time: `${route.type || ""}`.trim().toLowerCase() === "outbound"
          ? route.estimated_start_time || null
          : null,
        route_customer_list: cleanedCustomerList,
        checklist_result: route.checklist_result || []
      };
    });

    // Create the template payload
    const templatePayload = {
      name: templateName,
      template_date: templateDate,
      routes: cleanedRoutes,
      create_by: currentUser
    };

    // Call the service to create the template
    DailyRoutesTemplateService.createNewDailyRoutesTemplate(templatePayload)
      .then(() => {
        setSavingTemplate(false);
        closeSaveTemplateModal();
        setSuccessMessage(`Template "${templateName}" saved successfully!`);
        setTimeout(() => setSuccessMessage(undefined), 5000);
        // Refresh templates list
        DailyRoutesTemplateService.getAll().then((response) => {
          setTemplates(response.data || []);
        });
      })
      .catch(err => {
        setSavingTemplate(false);
        setErrorMessage(err.message || 'Failed to save template');
        setTimeout(() => setErrorMessage(undefined), 5000);
      });
  };

  useEffect(() => {
    return () => {
      if (scheduleImportProgressTimerRef.current) {
        clearInterval(scheduleImportProgressTimerRef.current);
      }
    };
  }, []);


  return (
    <>
      {pageLoading && <div className="spinner-overlay">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>}
      {isScheduleImporting && <div className="spinner-overlay" style={{ flexDirection: 'column', gap: '12px', zIndex: 1060 }}>
        <div style={{ width: '360px', maxWidth: '80vw', textAlign: 'center' }}>
          <div style={{ marginBottom: '8px', color: '#333' }}>{scheduleImportLabel || 'Importing schedule...'}</div>
          <ProgressBar now={scheduleImportProgress} animated label={`${Math.round(scheduleImportProgress)}%`} />
        </div>
      </div>}
      {routesOverviewLoading && currentTab === 'allRoutesOverview' && (
        <Modal
          show
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Body style={{ textAlign: 'center', padding: '22px 18px' }}>
            <div style={{ fontWeight: 600, color: '#333', marginBottom: '10px' }}>Loading routes…</div>
            <ProgressBar
              animated
              now={routesOverviewProgress || 50}
              label={`${routesOverviewProgress || 0}%`}
            />
          </Modal.Body>
        </Modal>
      )}
       <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Transportation Routes
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>
             All Routes - {moment(dateSelected).format('MM/DD/YYYY (dddd)')}
           </h4> 
        </div>
      </div>
      <div className="app-main-content-list-container">
				<div className="app-main-content-list-func-container">
					<Tabs defaultActiveKey="allRoutesOverview" id="routes-tab" onSelect={k => changeTab(k)}>
						<Tab eventKey="allRoutesOverview" title="Routes Overview">
              {!isFutureScheduleSelected && <div className="list row pt-4">
                {
                  showCopyDateTargetLoading ? <><Spinner></Spinner></> : <>
                    <div className="col-md-12 mb-4">
                      {overviewScheduleEditMode && AuthService.canAddOrEditRoutes() ? (
                        <RoutesSection
                          transRoutes={routesInboundForShowing}
                          copyList={routesOutboundForShowing}
                          addText="Add New Route"
                          copyText="Import Outbound Routes"
                          canAddNew={false}
                          drivers={drivers}
                          vehicles={vehicles}
                          redirect={goToCreateRoute}
                          routeType="inbound"
                          sectionName="Inbound Routes"
                          showRouteHeaderButtons
                          clearAllRoutesAction={() => triggerShowDeleteModal("inbound")}
                          fetchAfterCreate={overviewFetchAfterCreate}
                          clearConfirmMessage={ROUTE_CLEAR_INBOUND_CONFIRM}
                        />
                      ) : (
                        <RoutesSection
                          transRoutes={routesInboundForShowing}
                          drivers={drivers}
                          vehicles={vehicles}
                          sectionName="Inbound Routes"
                        />
                      )}
                    </div>
                    <div className="col-md-12 mb-4">
                      {overviewScheduleEditMode && AuthService.canAddOrEditRoutes() ? (
                        <RoutesSection
                          transRoutes={routesOutboundForShowing}
                          copyList={routesInboundForShowing}
                          addText="Add New Route"
                          copyText="Import Inbound Routes"
                          canAddNew={false}
                          drivers={drivers}
                          vehicles={vehicles}
                          redirect={goToCreateRoute}
                          routeType="outbound"
                          sectionName="Outbound Routes"
                          showRouteHeaderButtons
                          clearAllRoutesAction={() => triggerShowDeleteModal("outbound")}
                          fetchAfterCreate={overviewFetchAfterCreate}
                          clearConfirmMessage={ROUTE_CLEAR_OUTBOUND_CONFIRM}
                        />
                      ) : (
                        <RoutesSection
                          transRoutes={routesOutboundForShowing}
                          drivers={drivers}
                          vehicles={vehicles}
                          sectionName="Outbound Routes"
                        />
                      )}
                    </div>
                  </>
                }
                
              </div>}
              {
                isFutureScheduleSelected && <>
                  {errorMessage && <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {errorMessage}
                    <button onClick={() => setErrorMessage(undefined)} type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                  </div>}
                  {successMessage && <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {successMessage}
                    <button onClick={() => setSuccessMessage(undefined)} type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                  </div>}
                  <div className="list row pt-4">
                    {isLoading && <div className="col-md-12"><Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner></div>}
                    {!isLoading && <>
                      <div className="col-md-12 mb-4">
                        {(tmrInboundRoutes && tmrInboundRoutes.length > 0) || (tmrOutboundRoutes && tmrOutboundRoutes.length > 0) ? (
                          <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                            {/* <button className="btn btn-secondary btn-sm" onClick={() => navigate('/trans-routes/daily-templates/list')}>
                              View and Update Daily Route Templates
                            </button> */}
                          </div>
                        ) : null}
                          </div>
                      <div className="col-md-12 mb-4">
                        {isScheduleEditMode && AuthService.canAddOrEditRoutes() ? (
                          <RoutesSection
                            transRoutes={tmrInboundRoutes}
                            copyList={tmrOutboundRoutes}
                            addText="Add New Route"
                            copyText="Import Outbound Routes"
                            canAddNew={false}
                            drivers={drivers}
                            vehicles={vehicles}
                            redirect={goToCreateRoute}
                            routeType="inbound"
                            sectionName="Inbound Routes"
                            showRouteHeaderButtons
                            clearAllRoutesAction={() => triggerShowDeleteModal("inbound")}
                            fetchAfterCreate={{ fetchDate: moment(dateSelected).format("MM/DD/YYYY") }}
                            clearConfirmMessage={ROUTE_CLEAR_INBOUND_CONFIRM}
                          />
                        ) : (
                          <RoutesSection
                            transRoutes={tmrInboundRoutes}
                            drivers={drivers}
                            vehicles={vehicles}
                            sectionName="Inbound Routes"
                          />
                        )}
                      </div>
                      <hr />
                      <div className="col-md-12 mb-4">
                        {isScheduleEditMode && AuthService.canAddOrEditRoutes() ? (
                          <RoutesSection
                            transRoutes={tmrOutboundRoutes}
                            copyList={tmrInboundRoutes}
                            addText="Add New Route"
                            copyText="Import Inbound Routes"
                            canAddNew={false}
                            drivers={drivers}
                            vehicles={vehicles}
                            redirect={goToCreateRoute}
                            routeType="outbound"
                            sectionName="Outbound Routes"
                            showRouteHeaderButtons
                            clearAllRoutesAction={() => triggerShowDeleteModal("outbound")}
                            fetchAfterCreate={{ fetchDate: moment(dateSelected).format("MM/DD/YYYY") }}
                            clearConfirmMessage={ROUTE_CLEAR_OUTBOUND_CONFIRM}
                          />
                        ) : (
                          <RoutesSection
                            transRoutes={tmrOutboundRoutes}
                            drivers={drivers}
                            vehicles={vehicles}
                            sectionName="Outbound Routes"
                          />
                        )}
                      </div>
                      <hr />
                    </>}
                  </div>
                </>
              }
						</Tab>
						{!isFutureScheduleSelected && <Tab eventKey="allRoutesSignature" title="All Routes Signature">
            {routesSignatureLoading && (
              <div className="mb-3" style={{ maxWidth: '520px' }}>
                <ProgressBar animated now={50} label="Loading signatures..." />
              </div>
            )}
              <table className="personnel-info-table me-4">
                <thead>
                  <tr>
						        <th className="th-index">No.</th>
                    {
                      columns.map((column, index) => <th className="sortable-header" key={index}>
                        {column.label}  <span className="float-right" onClick={() => sortTableWithField(column.key)}><img src={`/images/${getSortingImg(column.key)}.png`}></img></span>
                      </th>)
                    }
                    <th>Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    (routesForSignature || [])
                      .filter((route) => {
                        if (!selectedDriver) return true;
                        return route?.driver === selectedDriver;
                      })
                      .filter((route) => {
                        // Hide rows without route type (e.g. type: '', null, undefined)
                        if (!route?.type) return false;
                        if (`${route.type}`.trim() === '') return false;
                        return true;
                      })
                      .filter((route) => {
                        if (!signatureRouteTypeFilter) return true;
                        return `${route?.type || ''}`.toLowerCase() === signatureRouteTypeFilter;
                      })
                      .filter((route) => {
                        if (!signatureHasSignatureFilter) return true;
                        const hasSignature = Boolean(route?.signature);
                        if (signatureHasSignatureFilter === 'yes') return hasSignature;
                        if (signatureHasSignatureFilter === 'no') return !hasSignature;
                        return true;
                      })
                      .filter((route) =>
                        route?.name?.toLowerCase().includes(keyword?.toLowerCase()) ||
                        getRouteDriverDisplayName(route?.driver)?.toLowerCase().includes(keyword?.toLowerCase())
                      )
                      .sort((a, b) => {
                        if (!sorting?.key) return 0;
                        const direction = sorting?.order === 'desc' ? -1 : 1;
                        if (sorting.key === 'name') {
                          return (a?.name || '').localeCompare(b?.name || '') * direction;
                        }
                        if (sorting.key === 'driver') {
                          const aName = getRouteDriverDisplayName(a?.driver);
                          const bName = getRouteDriverDisplayName(b?.driver);
                          return aName.localeCompare(bName) * direction;
                        }
                        if (sorting.key === 'estimated_start_time') {
                          const aT = a?.estimated_start_time ? moment(a.estimated_start_time).valueOf() : Number.POSITIVE_INFINITY;
                          const bT = b?.estimated_start_time ? moment(b.estimated_start_time).valueOf() : Number.POSITIVE_INFINITY;
                          return (aT - bT) * direction;
                        }
                        if (sorting.key === 'end_time') {
                          const aT = a?.end_time ? moment(a.end_time).valueOf() : Number.POSITIVE_INFINITY;
                          const bT = b?.end_time ? moment(b.end_time).valueOf() : Number.POSITIVE_INFINITY;
                          return (aT - bT) * direction;
                        }
                        if (sorting.key === 'type') {
                          return (`${a?.type || ''}`).localeCompare(`${b?.type || ''}`) * direction;
                        }
                        return 0;
                      })
                      ?.map(({ id, name, estimated_start_time, end_time, driver, type, signature }, index) => {
                        return (
                          <tr key={index}>
                        <td className="td-index">{index + 1}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link p-0"
                                style={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
                                onClick={() => goToRouteDetails(id)}
                              >
                                {name}
                              </button>
                            </td>
                        <td>{getRouteDriverDisplayName(driver)}</td>
                            <td>{estimated_start_time ? moment(estimated_start_time).format('HH:mm') : ''}</td>
                            <td>{end_time ? moment(end_time).format('HH:mm') : ''}</td>
                        <td>{type}</td>
                        <td>
                          {/* {images.find((img) => img.id === id)?.image && <img width="100px" src={`data:image/jpg;base64, ${images.find((img) => img.id === id)?.image}`}/>} */}
                              {signature && <img width="100px" src={`data:image/jpg;base64, ${signature}`} />}
                        </td>
                      </tr>
                        );
                        })
                      }
                    </tbody>
                  </table>
						</Tab>}
            {!isFutureScheduleSelected && <Tab eventKey="allRoutesStatus" title="All Routes Status">
              <div className="list row">
                <div className="col-md-12 mb-4">
                  <PersonnelSection transRoutes={routesForShowing} showCompletedInfo={false} 
                    showGroupInfo={false} allowForceEdit={AuthService.canAddOrEditRoutes()} 
                    showFilter={true} sectionName="Personnel Status (click on each user to edit)" 
                    vehicles={vehicles} keyword={keyword}
                    statusFilter={statusFilter}
                    customerTypeFilter={customerTypeFilter}
                    customerNameFilter={customerNameFilter}
                    customerTableId={customerTableId}
                    routeTypeFilter={routeTypeFilter}
                    hideDischargedMembers={true}
                    dischargedCustomerIds={dischargedCustomerIds}
                    hideEmptyRouteTypeRows={true}
                    showInboundParticipantsReportButton={false}
                  />
                </div>
              </div>
						</Tab>}
					</Tabs>
					<div className="list-func-panel">
            {
              currentTab === 'allRoutesOverview' &&  <> {
                !showCopyDateTargetLoading && <>
                  {!hideExportAndViewByDateInScheduleEdit && AuthService.canExportTransportationScheduleReport() && (
                    <Dropdown className="me-2">
                      <Dropdown.Toggle variant="outline-secondary" disabled={isExportingRouteReport}>
                        <Download size={16} className="me-2" aria-hidden />
                        Export
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => generateRouteReport()} disabled={isExportingRouteReport}>
                          Route PDF Report
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => generateDietaryRecordsExcel()} disabled={isExportingRouteReport}>
                          Dietary Records Excel
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => void exportDepartureBoardExcel()} disabled={isExportingRouteReport}>
                          Departure Board
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                  {!hideExportAndViewByDateInScheduleEdit && (
                  <Dropdown
                    key={'signature-date'}
                    id="signature-date"
                    className="me-2"
                    show={showOriginDateDropdown}
                    onToggle={() => setShowOriginDateDropdown(!showOriginDateDropdown)}
                    autoClose={false}
                  > 
                    <Dropdown.Toggle variant="outline-secondary">
                      <CalendarWeek size={16} className="me-2"></CalendarWeek>View By Date
                    </Dropdown.Toggle>
                    <Dropdown.Menu as={customMenuOriginDate}/> 
                  </Dropdown>
                  )}
                  {isTodayView && AuthService.canAddOrEditRoutes() && !isScheduleEditMode && (
                    <button type="button" className="btn btn-primary me-2" onClick={() => directToSchedule()}>
                      <CalendarCheck size={16} className="me-2" aria-hidden />
                      Plan Tomorrow's Routes
                    </button>
                  )}
                  {isFutureScheduleSelected && AuthService.canAddOrEditRoutes() && isScheduleEditMode && <>
                  <Dropdown
                      key={'import-from-date'}
                      id="import-from-date"
                    className="me-2"
                      show={showImportDateDropdown}
                      onToggle={() => setShowImportDateDropdown(!showImportDateDropdown)}
                    autoClose={false}
                  > 
                      <Dropdown.Toggle variant="outline-secondary" disabled={isScheduleImporting}>
                        <Copy size={16} className="me-2"></Copy>Import From Date
                    </Dropdown.Toggle>
                      <Dropdown.Menu as={customMenuImportFromDate}/>
                  </Dropdown>
                    <Dropdown
                      key={'import-from-template'}
                      id="import-from-template"
                      className="me-2"
                      show={showImportTemplateDropdown}
                      onToggle={() => setShowImportTemplateDropdown(!showImportTemplateDropdown)}
                      autoClose={false}
                    >
                      <Dropdown.Toggle variant="outline-secondary" disabled={isScheduleImporting}>
                        <Copy size={16} className="me-2"></Copy>Import From Template
                      </Dropdown.Toggle>
                      <Dropdown.Menu as={customMenuImportFromTemplate}/>
                    </Dropdown>
                  </>}
                  </>
                }
                {AuthService.canAddOrEditRoutes() && !isScheduleEditMode && (
                  <button className="btn btn-primary me-2" type="button" onClick={() => setScheduleEditInUrl(true)}>
                    <PencilSquare size={16} className="me-2" aria-hidden />Edit Schedule
                  </button>
                )}
                {AuthService.canAddOrEditRoutes() && isScheduleEditMode && isTodayView && (
                  <button
                    className="btn btn-outline-secondary me-2"
                    type="button"
                    onClick={() => goToCreateRoute()}
                  >
                    <Plus size={16} className="me-2"></Plus>Add New Route
                  </button>
                )}
                {AuthService.canAddOrEditRoutes() && isScheduleEditMode && isTodayView && (
                  <button
                    className="btn btn-primary me-2"
                    type="button"
                    onClick={() =>
                      runCheckRoutes({
                        inbound: routesInboundForShowing,
                        outbound: routesOutboundForShowing
                      })
                    }
                  >
                    <Check size={16} className="me-2" aria-hidden />
                    Check Routes
                  </button>
                )}
                {AuthService.canAddOrEditRoutes() && isScheduleEditMode && isFutureScheduleSelected && (
                  <button
                    className="btn btn-outline-secondary me-2"
                    type="button"
                    onClick={() => goToCreateRoute()}
                  >
                    <Plus size={16} className="me-2"></Plus>Add New Route
                  </button>
                )}
                {AuthService.canAddOrEditRoutes() && isScheduleEditMode && isFutureScheduleSelected && (
                  <button
                    className="btn btn-primary me-2"
                    type="button"
                    onClick={() => runCheckRoutes()}
                    disabled={isScheduleImporting}
                  >
                    <Check size={16} className="me-2" aria-hidden />
                    Check Routes
                  </button>
                )}
                {!isFutureScheduleSelected && AuthService.canAddOrEditRoutes() && isScheduleEditMode && !isTodayView && (
                  <button className="btn btn-outline-secondary me-2" type="button" onClick={() => goToCreateRoute()}>
                    <Plus size={16} className="me-2"></Plus>Add New Route
                  </button>
                )}
                {AuthService.canAddOrEditRoutes() && isScheduleEditMode && (
                  <button className="btn btn-primary me-2" type="button" onClick={() => setScheduleEditInUrl(false)}>
                    Done editing schedule
                  </button>
                )}
              </>
            }

            { currentTab === 'allRoutesSignature' && <>
              <input
                className="me-2 with-search-icon"
                type="text"
                placeholder="Search"
                value={keyword}
                onChange={(e) => setKeyword(e.currentTarget.value)}
              />
              <Dropdown
                key={'signature-date'}
                id="signature-date"
                className="me-2"
                show={showDateDropdown}
                onToggle={(nextOpen) => setShowDateDropdown(nextOpen)}
                autoClose={false}
              > 
                <Dropdown.Toggle variant="outline-secondary">
                  <CalendarWeek size={16} className="me-2"></CalendarWeek>Select Date to View Report
                </Dropdown.Toggle>
                <Dropdown.Menu
                  as={RoutesScheduleViewDateMenu}
                  dateSelected={dateSelected}
                  onSelectViewDate={(v) => {
                    setDateSelected(v);
                    setShowDateDropdown(false);
                  }}
                />
              </Dropdown>
              <Dropdown
                key={'filter-signature'}
                id="filter-signature"
                className="me-2"
                show={showFilterDropdown}
                onToggle={() => setShowFilterDropdown(!showFilterDropdown)}
                autoClose={false}
              > 
                <Dropdown.Toggle variant="outline-secondary">
                  <Filter size={16} className="me-2"></Filter>Filter
                </Dropdown.Toggle>
                <Dropdown.Menu as={customMenu}/> 
              </Dropdown>
            </> }

            { currentTab === 'allRoutesStatus' && <>
              {/* <input className="me-2 with-search-icon" type="text" placeholder="Search" value={keyword}  onChange={(e) => setKeyword(e.currentTarget.value)} /> */}
              <Dropdown
                key={'status-date'}
                id="status-date"
                className="me-2"
                show={showDateDropdown}
                onToggle={(nextOpen) => setShowDateDropdown(nextOpen)}
                autoClose={false}
              > 
                <Dropdown.Toggle variant="outline-secondary">
                  <CalendarWeek size={16} className="me-2"></CalendarWeek>Select Date to View Report
                </Dropdown.Toggle>
                <Dropdown.Menu
                  as={RoutesScheduleViewDateMenu}
                  dateSelected={dateSelected}
                  onSelectViewDate={(v) => {
                    setDateSelected(v);
                    setShowDateDropdown(false);
                  }}
                />
              </Dropdown>
              <Dropdown
                key={'filter-report'}
                id="filter-report"
                className="me-2"
                show={showFilterReportDropdown}
                onToggle={(nextOpen) => setShowFilterReportDropdown(nextOpen)}
                autoClose={false}
              > 
                <Dropdown.Toggle variant="outline-secondary">
                  <Filter size={16} className="me-2"></Filter>Filter
                </Dropdown.Toggle>
                <Dropdown.Menu as={customReportFilterMenu}/> 
              </Dropdown>
              {AuthService.canExportTransportationScheduleReport() && (
              <button
                type="button"
                className="btn btn-primary"
                disabled={isExportingRouteReport}
                onClick={() => void exportInboundParticipantsReportFromStatusTab()}
              >
                <Download size={16} className="me-2" aria-hidden />
                Export
              </button>
              )}
            </> }
            
					</div>
          <Modal show={showDeleteModal} onHide={() => closeDeleteModal()}>
            <Modal.Header closeButton>
              <Modal.Title>Disable Routes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                {deleteRouteType === 'inbound'
                  ? 'Are you sure you want to disable all inbound routes for this date?'
                  : deleteRouteType === 'outbound'
                    ? 'Are you sure you want to disable all outbound routes for this date?'
                    : 'Are you sure you want to disable all routes for this date?'}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => closeDeleteModal()}>
                No
              </Button>
              <Button variant="primary" onClick={() => cleanupSchedule()}>
                Yes
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showSaveTemplateModal} onHide={closeSaveTemplateModal}>
            <Modal.Header closeButton>
              <Modal.Title>Save Routes as Template</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <label className="form-label">Template Name</label>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Enter template name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  disabled={savingTemplate}
                />
              </div>
              <div className="text-muted">
                <small>This will save all routes from {getDateString(dateSelected)} as a reusable template.</small>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeSaveTemplateModal} disabled={savingTemplate}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveRoutesAsTemplate} disabled={savingTemplate}>
                {savingTemplate ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Submit'}
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal
            show={showImportAddressSuggestionsModal}
            onHide={() => {
              if (!importAddressApplying) ignoreImportAddressSuggestions();
            }}
            backdrop={importAddressApplying ? 'static' : true}
          >
            <Modal.Header closeButton={!importAddressApplying}>
              <Modal.Title>Suggested Address Fixes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-3">
                After import, some route addresses do not match the customer&apos;s current profile addresses.
                Apply suggested fixes, or ignore to keep the imported addresses.
              </p>
              {importAddressSuggestions.map((issue, idx) => (
                <div key={`import-address-suggestion-${idx}`} className="mb-3" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  <div className="fw-bold">{issue.customerName}</div>
                  <div className="mt-1">
                    Route address on {joinRouteNames(issue.routeNames)}:
                    <div>{issue.routeAddress || 'N/A'}</div>
                  </div>
                  <div className="mt-1">
                    Suggested address:
                    <div>{issue.suggestedAddress || 'N/A'}</div>
                  </div>
                </div>
              ))}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={ignoreImportAddressSuggestions}
                disabled={importAddressApplying}
              >
                Ignore
              </Button>
              <Button
                variant="primary"
                onClick={() => void applyImportAddressSuggestions()}
                disabled={importAddressApplying}
              >
                {importAddressApplying ? (
                  <><Spinner size="sm" className="me-2" />Applying...</>
                ) : (
                  'Apply'
                )}
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal show={showCheckRoutesModal} onHide={() => setShowCheckRoutesModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Check Routes Result</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {(checkRoutesResult.inbound.length === 0 && checkRoutesResult.outbound.length === 0 && checkRoutesResult.attendance.length === 0 && checkRoutesResult.addressMismatch.length === 0 && checkRoutesResult.customerTypeMismatch.length === 0 && checkRoutesResult.customerSpecialNeedsMismatch.length === 0 && checkRoutesResult.customerNoteMismatch.length === 0 && checkRoutesResult.dischargedOnRoute.length === 0) ? (
                <div className="text-success d-flex align-items-center">
                  <Check size={18} className="me-2"></Check>
                  <span>No issue found.</span>
				</div>
              ) : (
                <>
                  {checkRoutesResult.inbound.length > 0 && (
                    <div className="mb-3">
                      <div className="fw-bold mb-2">Inbound Issues</div>
                      {checkRoutesResult.inbound.map((issue, idx) => (
                        <div key={`inbound-issue-${idx}`} className="mb-2">
                          {issue.customerName} appears on multiple routes: {formatRouteAddressList(issue.routes)}
      </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.outbound.length > 0 && (
                    <div>
                      <div className="fw-bold mb-2">Outbound Issues</div>
                      {checkRoutesResult.outbound.map((issue, idx) => (
                        <div key={`outbound-issue-${idx}`} className="mb-2">
                          {issue.customerName} appears on multiple routes: {formatRouteAddressList(issue.routes)}
                        </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.attendance.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-2">Attendance Note Issues</div>
                      {checkRoutesResult.attendance.map((issue, idx) => (
                        <div key={`attendance-issue-${idx}`} className="mb-2">
                          {issue.customerName} is scheduled absent on {issue.noteDate} but still appears on Route {joinRouteNames(issue.routeNames)}.
                        </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.addressMismatch.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-2">Address Mismatch Issues</div>
                      {checkRoutesResult.addressMismatch.map((issue, idx) => (
                        <div key={`address-mismatch-issue-${idx}`} className="mb-2">
                          Customer {issue.customerName}&apos;s address: {issue.routeAddress || 'N/A'} is not matching his existing addresses stored in system, on route {joinRouteNames(issue.routeNames)}.
                          {issue.suggestedAddress ? (
                            <div className="mt-1 text-muted">Suggested: {issue.suggestedAddress}</div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.customerTypeMismatch.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-2">Member Type Mismatch Issues</div>
                      {checkRoutesResult.customerTypeMismatch.map((issue, idx) => (
                        <div key={`customer-type-mismatch-issue-${idx}`} className="mb-2 d-flex align-items-center justify-content-between" style={{ gap: '12px' }}>
                          <div>
                            Customer {issue.customerName}&apos;s member type is mismatched on route {joinRouteNames(issue.mismatchedRoutes.map((route) => route.routeName))}. Latest type in system is {getCustomerTypeLabel(issue.dbType)}.
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!!customerTypeFixing[issue.customerId]}
                            onClick={() => syncCustomerTypeForMismatch(issue)}
                          >
                            {customerTypeFixing[issue.customerId] ? 'Fixing...' : 'Fix'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.customerSpecialNeedsMismatch.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-2">Note to Driver Mismatch Issues</div>
                      {checkRoutesResult.customerSpecialNeedsMismatch.map((issue, idx) => (
                        <div key={`customer-special-needs-mismatch-issue-${idx}`} className="mb-2" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                          <div>
                            Customer {issue.customerName}&apos;s note to driver is mismatched on route {joinRouteNames(issue.mismatchedRoutes.map((route) => route.routeName))}.
                          </div>
                          <div className="mt-1">
                            Latest note in system:
                            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {issue.dbNoteToDriver || 'empty'}
                            </div>
                          </div>
                          <div className="mt-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!!customerSpecialNeedsFixing[issue.customerId]}
                              onClick={() => syncCustomerSpecialNeedsForMismatch(issue)}
                            >
                              {customerSpecialNeedsFixing[issue.customerId] ? 'Fixing...' : 'Fix'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.customerNoteMismatch.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-2">Route Note Mismatch Issues</div>
                      {checkRoutesResult.customerNoteMismatch.map((issue, idx) => (
                        <div key={`customer-note-mismatch-issue-${idx}`} className="mb-2" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                          <div>
                            Customer {issue.customerName}&apos;s route note is mismatched on route {joinRouteNames(issue.mismatchedRoutes.map((route) => route.routeName))}.
                          </div>
                          <div className="mt-1">
                            Latest note in system:
                            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {issue.dbNoteToDriver || 'empty'}
                            </div>
                          </div>
                          <div className="mt-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={!!customerNoteFixing[issue.customerId]}
                              onClick={() => syncCustomerNoteForMismatch(issue)}
                            >
                              {customerNoteFixing[issue.customerId] ? 'Fixing...' : 'Fix'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {checkRoutesResult.dischargedOnRoute.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-2">Discharged Customer Issues</div>
                      {checkRoutesResult.dischargedOnRoute.map((issue, idx) => (
                        <div key={`discharged-route-issue-${idx}`} className="mb-2">
                          Customer {issue.customerName} has been discharged and he is still on route {joinRouteNames(issue.routeNames)}.
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => setShowCheckRoutesModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={isExportingRouteReport} backdrop="static" keyboard={false} centered>
            <Modal.Header>
              <Modal.Title>Generating export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-2">Please wait. User interactions are temporarily disabled.</div>
              <ProgressBar now={exportRouteReportProgress} animated label={`${Math.round(exportRouteReportProgress)}%`} />
            </Modal.Body>
          </Modal>
				</div>
      </div>


      {/* <div className="list row mb-4">
        <div className=" text-primary mb-2">
          Today's Date: { new Date().toLocaleDateString() }
        </div>
        <div className="text-primary mb-2">
          <button type="button" className="btn btn-primary btn-sm me-1 mb-2" onClick={()=> syncCustomersInfo()}>{showSyncCustomersLoading? 'Loading...' : `Sync Customers Data`}</button>
          <button type="button" className="btn btn-primary btn-sm me-1 mb-2" onClick={()=> generateRouteReport()}>Generate Route Report</button>
          {AuthService.canAddOrEditRoutes() &&
            <button type="button" className="btn btn-primary btn-sm me-1 mb-2" onClick={() => directToSchedule()}>Plan Tomorrow's Route Schedule</button>
          }
          <button type="button" className="btn btn-primary btn-sm me-1 mb-2" onClick={()=> createNewRoute()}>Create New Route</button>
          <button type="button" className="btn btn-secondary btn-sm me-1 mb-2" onClick={()=> goToHistoryPage()}>View History</button>
          <button type="button" className="btn btn-secondary btn-sm me-1 mb-2" onClick={()=> goToSignature()}>View Routes Signature</button>
        </div>
      </div> */}
    </>
    
  );
};

export default RoutesDashboard;